import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export const videos = [
    { src: "/videos/launch.mp4", title: "Launch" },
    { src: "/videos/app.mp4", title: "App" },
    { src: "/videos/market.mp4", title: "Market" },
];

const Videos = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialVideoIndex = parseInt(searchParams.get("v") || "0");
    const [currentIndex, setCurrentIndex] = React.useState(initialVideoIndex);
    const videoRef = React.useRef<HTMLVideoElement>(null);

    // Auto-play video on load
    React.useEffect(() => {
        if (videoRef.current) {
            videoRef.current.play().catch(() => {
                // Autoplay might be blocked
            });
        }
    }, [currentIndex]);

    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1));
    };

    const handleVideoEnd = () => {
        // Auto-advance to next video when current one ends
        handleNext();
    };

    return (
        <div className="min-h-screen w-full bg-black relative">
            {/* Home Button */}
            <button
                onClick={() => navigate("/home")}
                className="fixed top-4 left-4 z-50 p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-95 group"
                style={{
                    backgroundColor: "#DB1C7D",
                }}
                title="Back to Home"
            >
                <svg className="w-6 h-6 text-white transition-transform duration-300 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                </svg>
                {/* Tooltip */}
                <span
                    className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                    style={{ boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }}
                >
                    Back to Home
                    <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" style={{ marginRight: "-1px" }}></span>
                </span>
            </button>

            {/* Main Content */}
            <div className="relative h-screen flex items-center justify-center">
                {/* Previous Button */}
                <button
                    onClick={handlePrevious}
                    className="absolute left-4 md:left-8 z-40 p-3 md:p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-95"
                    style={{
                        backgroundColor: "rgba(219, 28, 125, 0.9)",
                        top: "50%",
                        transform: "translateY(-50%)",
                    }}
                    title="Previous"
                >
                    <svg className="w-6 h-6 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                {/* Video Display */}
                <div className="relative w-full h-full flex flex-col items-center justify-center px-4 md:px-16">
                    <div className="relative w-full max-w-6xl">
                        <video ref={videoRef} key={currentIndex} className="w-full h-auto max-h-[80vh] rounded-lg shadow-2xl" controls onEnded={handleVideoEnd} playsInline>
                            <source src={videos[currentIndex].src} type="video/mp4" />
                            Your browser does not support the video tag.
                        </video>

                        {/* Video Info */}
                        <div className="mt-4 text-center">
                            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">{videos[currentIndex].title}</h2>
                        </div>
                    </div>
                </div>

                {/* Next Button */}
                <button
                    onClick={handleNext}
                    className="absolute right-4 md:right-8 z-40 p-3 md:p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-95"
                    style={{
                        backgroundColor: "rgba(219, 28, 125, 0.9)",
                        top: "50%",
                        transform: "translateY(-50%)",
                    }}
                    title="Next"
                >
                    <svg className="w-6 h-6 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>

            {/* Thumbnail Navigation */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-40 bg-gray-900 bg-opacity-90 backdrop-blur-sm rounded-xl p-3 shadow-2xl">
                <div className="flex gap-3 items-center">
                    <span className="text-white text-xs font-medium px-2 hidden sm:block">Videos:</span>
                    <div className="flex gap-2">
                        {videos.map((video, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentIndex(index)}
                                className={`relative group transition-all duration-300 rounded-lg overflow-hidden ${
                                    index === currentIndex ? "ring-4 ring-pink-500 scale-110" : "opacity-70 hover:opacity-100 hover:scale-105"
                                }`}
                                style={{ width: "80px", height: "60px" }}
                                title={video.title}
                            >
                                <video className="w-full h-full object-cover pointer-events-none" src={video.src} muted />
                                {/* Overlay */}
                                <div
                                    className={`absolute transition-opacity duration-300 ${
                                        index === currentIndex ? "bg-pink-500 bg-opacity-0" : "bg-black bg-opacity-30 group-hover:bg-opacity-10"
                                    }`}
                                ></div>
                                {/* Number Badge */}
                                <div
                                    className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                                        index === currentIndex ? "bg-pink-500 text-white" : "bg-gray-800 bg-opacity-75 text-white"
                                    }`}
                                >
                                    {index + 1}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Video Counter */}
            <div className="absolute top-20 right-4 z-40 bg-gray-900 bg-opacity-75 text-white px-4 py-2 rounded-lg text-sm font-medium">
                {currentIndex + 1} / {videos.length}
            </div>
        </div>
    );
};

export default Videos;
